import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { ProjectVariable } from '@mockd/shared';
import { api, ApiError } from '../utils/apiClient';

interface UseProjectVariablesReturn {
  variables: ProjectVariable[];
  loading: boolean;
  error: string | null;
  upgradeRequired: boolean;
  fetchProjectVariables: () => Promise<void>;
  createProjectVariable: (key: string, value: unknown) => Promise<ProjectVariable>;
  updateProjectVariable: (id: string, key: string, value: unknown) => Promise<ProjectVariable>;
  deleteProjectVariable: (id: string) => Promise<void>;
}

export function useProjectVariables(projectId: string): UseProjectVariablesReturn {
  const [variables, setVariables] = useState<ProjectVariable[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);

  const fetchProjectVariables = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const json = await api.get<{ data: ProjectVariable[]; upgradeRequired?: boolean }>(
        `/api/projects/${projectId}/variables`
      );
      setVariables(json.data || []);
      setUpgradeRequired(json.upgradeRequired ?? false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch project variables');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const createProjectVariable = useCallback(async (
    key: string,
    value: unknown,
  ): Promise<ProjectVariable> => {
    try {
      const json = await api.post<{ data: ProjectVariable }>(
        `/api/projects/${projectId}/variables`,
        { key, value }
      );
      const newVar = json.data;
      setVariables(prev => [...prev, newVar]);
      toast.success('Project variable created');
      return newVar;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create project variable';
      toast.error(message);
      throw err;
    }
  }, [projectId]);

  const updateProjectVariable = useCallback(async (
    id: string,
    key: string,
    value: unknown,
  ): Promise<ProjectVariable> => {
    try {
      const json = await api.put<{ data: ProjectVariable }>(
        `/api/projects/${projectId}/variables/${id}`,
        { key, value }
      );
      const updated = json.data;
      setVariables(prev => prev.map(v => v.id === id ? updated : v));
      toast.success('Project variable updated');
      return updated;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to update project variable';
      toast.error(message);
      throw err;
    }
  }, [projectId]);

  const deleteProjectVariable = useCallback(async (id: string): Promise<void> => {
    try {
      await api.del(`/api/projects/${projectId}/variables/${id}`);
      setVariables(prev => prev.filter(v => v.id !== id));
      toast.success('Project variable deleted');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete project variable';
      toast.error(message);
      throw err;
    }
  }, [projectId]);

  return {
    variables,
    loading,
    error,
    upgradeRequired,
    fetchProjectVariables,
    createProjectVariable,
    updateProjectVariable,
    deleteProjectVariable,
  };
}
